<script setup lang="ts">
  import { computed } from 'vue';
  import { IconRadio, NumberStepperInput } from '@/shared/ui';
  import { RETENTION_OPTIONS } from '../../lib/record-config-options';
  import { rejectNonIntegerKeys, toWholeNumber } from '../../lib/whole-number-input';
  import type { RetentionMode } from '../../model/record-config.types';
  import RecordConfigRepeater from './RecordConfigRepeater.vue';

  const RADIO_GROUP = 'record-config-retention';
  const MIN_MONTHS = 1;
  const MONTHS_PLACEHOLDER = 'Prazo em meses';
  const MONTHS_INPUT_CLASS = 'h-12 w-full bg-white-01 text-gray-07 placeholder:text-gray-03';

  const props = defineProps<{
    retention: RetentionMode | null;
    retentionMonths: number | null;
    retentionPeriods: Array<number | null>;
    isErpOrigin: boolean;
  }>();

  const emit = defineEmits<{
    'update:retention': [value: RetentionMode];
    'update:retentionMonths': [value: number | null];
    'update:retentionPeriods': [value: Array<number | null>];
  }>();

  const selected = computed(() => props.retention ?? undefined);
  const showsMonths = computed(() => props.retention === 'DEFINED' && !props.isErpOrigin);
  const showsArchive = computed(() => props.retention === 'DEFINED' && props.isErpOrigin);

  function toWholeMonths(value: number | null): number | null {
    return toWholeNumber(value, MIN_MONTHS);
  }
</script>

<template>
  <section class="flex flex-col gap-3 px-8 py-6" data-testid="record-config-retention-section">
    <span class="text-sm font-semibold text-gray-07">Retenção:</span>
    <div class="flex flex-wrap gap-x-8 gap-y-3">
      <IconRadio
        v-for="option in RETENTION_OPTIONS"
        :key="option.id"
        :model-value="selected"
        :value="option.id"
        :label="option.name"
        :name="RADIO_GROUP"
        variant="medium"
        @update:model-value="emit('update:retention', $event)"
      />
    </div>

    <NumberStepperInput
      v-if="showsMonths"
      input-id="record-config-retention-months"
      :model-value="retentionMonths"
      :min="MIN_MONTHS"
      :step="1"
      :placeholder="MONTHS_PLACEHOLDER"
      aria-label="Prazo em meses"
      wrapper-class="w-44"
      :input-class="MONTHS_INPUT_CLASS"
      @keydown="rejectNonIntegerKeys"
      @update:model-value="emit('update:retentionMonths', toWholeMonths($event))"
    />

    <div
      v-else-if="showsArchive"
      class="flex flex-col gap-3"
      data-testid="record-config-retention-archive"
    >
      <span class="text-sm font-semibold text-gray-07">Arquivo morto</span>
      <RecordConfigRepeater
        :model-value="retentionPeriods"
        :empty-value="null"
        class="max-w-72"
        @update:model-value="emit('update:retentionPeriods', $event)"
      >
        <template #default="{ value, index, update }">
          <NumberStepperInput
            :input-id="`record-config-retention-period-${index}`"
            :model-value="value"
            :min="MIN_MONTHS"
            :step="1"
            :placeholder="MONTHS_PLACEHOLDER"
            aria-label="Prazo em meses"
            wrapper-class="w-44"
            :input-class="MONTHS_INPUT_CLASS"
            @keydown="rejectNonIntegerKeys"
            @update:model-value="update(toWholeMonths($event))"
          />
        </template>
      </RecordConfigRepeater>
    </div>
  </section>
</template>
